import { defineStore } from 'pinia'
import authService from '@/services/authService'
import roleService from '@/services/roleService'
import { clearRedirect } from '@/utils/redirectAfterLogin'

const LS_TOKEN = 'access_token'
const LS_ESTABLISHMENT = 'logelite:establishment'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    currentUser: null,
    token: localStorage.getItem(LS_TOKEN),
    isAuthenticated: !!localStorage.getItem(LS_TOKEN),
    establishments: [],
    currentEstablishmentId: localStorage.getItem(LS_ESTABLISHMENT) || null
  }),

  getters: {
    isEstablishmentBypassed: (state) => {
      try {
        if (!state.token) return false
        const payload = JSON.parse(atob(state.token.split('.')[1]))
        const role = payload.role || ''
        return [
          'superadmin',
          'super_admin',
          'SUPERADMIN',
          'SUPER_ADMIN',
          'laboratoire',
          'Laboratoire'
        ].includes(role)
      } catch {
        return false
      }
    },

    currentEstablishment: (state) => {
      return state.establishments.find((e) => e.id === state.currentEstablishmentId) ?? null
    },

    needsEstablishmentSelection: (state) => {
      return state.establishments.length > 1 && !state.currentEstablishmentId
    },

    hasMultipleEstablishments: (state) => state.establishments.length > 1
  },

  actions: {
    async login(credentials) {
      try {
        // Réinitialiser l'état avant la tentative de connexion
        this.currentUser = null
        this.token = null
        this.isAuthenticated = false

        const response = await authService.login(credentials.email, credentials.password)

        if (!response?.access_token) {
          throw new Error('Invalid response format - no access token received')
        }

        this.token = response.access_token
        this.isAuthenticated = true
        localStorage.setItem(LS_TOKEN, this.token)

        await this.fetchCurrentUser()
        this._initEstablishments()

        if (!this.currentUser) {
          throw new Error('Failed to fetch user data after successful authentication')
        }

        return response
      } catch (error) {
        this.currentUser = null
        this.token = null
        this.isAuthenticated = false
        this.establishments = []
        this.currentEstablishmentId = null
        localStorage.removeItem(LS_TOKEN)
        localStorage.removeItem(LS_ESTABLISHMENT)
        throw error
      }
    },

    async fetchCurrentUser() {
      const response = await authService.getCurrentUser()

      let userData = response.data

      if (userData && typeof userData === 'object' && userData.data) {
        userData = userData.data
      }

      if (!userData) {
        throw new Error('No user data received from server')
      }

      this.currentUser = userData

      if (!this.currentUser.role_details) {
        this.currentUser.role_details = {
          name: null,
          permissions: []
        }
      }

      const jwtRole = this.getJWTRole()

      if (jwtRole) {
        this.currentUser.role = jwtRole
        this.currentUser.role_details.name = jwtRole
      }

      if (this.currentUser?.role_id) {
        await this.fetchUserRole(this.currentUser.role_id)
      } else {
        const jwtPermissions = this.getJWTPermissions()
        if (jwtPermissions.length > 0) {
          this.currentUser.role_details.permissions = jwtPermissions.map((p) => ({ name: p }))
        }
      }

      // Toujours (re)hydrater les établissements depuis le JWT : sans ça,
      // au rafraîchissement de page (guard router / App.vue qui appellent
      // fetchCurrentUser sans passer par login), la liste reste vide et
      // l'établissement courant ne s'affiche plus.
      this._initEstablishments()

      return this.currentUser
    },

    async fetchUserRole(roleId) {
      try {
        const response = await roleService.getRoleWithPermissions(roleId)

        // Vérifier si la réponse est du HTML au lieu de JSON
        if (typeof response === 'string' && response.includes('<!DOCTYPE html>')) {
          throw new Error('API configuration error - receiving HTML instead of JSON')
        }

        if (Array.isArray(response)) {
          this.currentUser.role_details = {
            ...this.currentUser.role_details,
            name: this.currentUser.role || this.getJWTRole(),
            permissions: response
          }
        }

        return response
      } catch (error) {
        // En cas d'erreur, utiliser les permissions du JWT si disponibles
        if (this.currentUser?.role || this.getJWTRole()) {
          this.currentUser.role_details = {
            ...this.currentUser.role_details,
            name: this.currentUser.role || this.getJWTRole(),
            permissions: this.getJWTPermissions().map((p) => ({ name: p }))
          }
        }

        // Ne pas relancer l'erreur pour éviter de bloquer l'application
        return null
      }
    },

    _initEstablishments() {
      try {
        if (!this.token) return
        const payload = JSON.parse(atob(this.token.split('.')[1]))
        this.establishments = payload.establishments || []
        if (this.establishments.length === 1) {
          this.currentEstablishmentId = this.establishments[0].id
          localStorage.setItem(LS_ESTABLISHMENT, this.currentEstablishmentId)
        } else if (this.establishments.length > 1) {
          const saved = localStorage.getItem(LS_ESTABLISHMENT)
          const valid = saved && this.establishments.some((e) => e.id === saved)
          this.currentEstablishmentId = valid ? saved : null
          if (!valid) localStorage.removeItem(LS_ESTABLISHMENT)
        }
      } catch {
        /* ignore */
      }
    },

    setCurrentEstablishment(id) {
      this.currentEstablishmentId = id
      localStorage.setItem(LS_ESTABLISHMENT, id)
    },

    getJWTRole() {
      try {
        const token = this.token
        if (!token) return ''

        const payload = JSON.parse(atob(token.split('.')[1]))
        return payload.role || ''
      } catch (error) {
        return ''
      }
    },

    getJWTPermissions() {
      try {
        const token = this.token
        if (!token) return []

        const payload = JSON.parse(atob(token.split('.')[1]))
        return payload.permissions || []
      } catch (error) {
        return []
      }
    },

    async checkAuth() {
      const token = localStorage.getItem(LS_TOKEN)
      if (token && !this.currentUser) {
        this.token = token
        this.isAuthenticated = true
        try {
          await this.fetchCurrentUser()
          this._initEstablishments()
        } catch (error) {
          this.logout()
        }
      }
    },

    logout() {
      this.currentUser = null
      this.token = null
      this.isAuthenticated = false
      this.establishments = []
      this.currentEstablishmentId = null
      localStorage.removeItem(LS_TOKEN)
      localStorage.removeItem(LS_ESTABLISHMENT)
      // Sinon le compte suivant à se connecter sur ce navigateur hériterait
      // de la destination du précédent.
      clearRedirect()
      authService.logout()
    },

    isUserDataComplete() {
      return (
        this.currentUser &&
        this.currentUser.role_details &&
        this.currentUser.role_details.name &&
        this.currentUser.role_details.permissions
      )
    }
  }
})
